<script lang="ts" setup>
  import Button from '@leaflink/stash/Button.vue';
  import Card from '@leaflink/stash/Card.vue';
  import CardContent from '@leaflink/stash/CardContent.vue';
  import Dialog from '@leaflink/stash/Dialog.vue';
  import Divider from '@leaflink/stash/Divider.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import PlaidLink from '@leaflink/stash/PlaidLink.vue';
  import Skeleton from '@leaflink/stash/Skeleton.vue';
  import { useQuery } from '@tanstack/vue-query';
  import isEqual from 'lodash-es/isEqual';
  import { onBeforeMount, onUnmounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { useFeatureEnrollmentProvider } from '@/components/FeatureEnrollment/FeatureEnrollmentLayout.vue';
  import { LEAFLINK_PLUS_ID } from '@/constants';
  import { env } from '@/constants/env';
  import { BillingPaymentMethod } from '@/models/billing';
  import AddCreditCard from '@/pages/Settings/Billing/components/AddCreditCard.vue';
  import PaymentMethodListCard from '@/pages/Settings/Billing/components/PaymentMethodListCard.vue';
  import { useSettingsStore } from '@/piniaStores/settings/settings.store';
  import { i18n } from '@/plugins';
  import { useDraftOrdersPost, useProductsQuery } from '@/queries/feature-enrollment/feature-enrollment.query';
  import { trackWithSegment } from '@/segment';
  import { postLinkToken } from '@/services/api/plaid';
  import { createCompanyBankAccount, getCompanyBillingWithPost } from '@/services/api/settings';
  import { isHttpError, notifyError } from '@/utils/errors';
  import sortItems from '@/utils/sortItems';

  import BillingContactModal from '../components/BillingContactModal.vue';
  import { useInventoryInsightsProvider } from '../InventoryInsights.vue';

  const {
    paymentMethods,
    company,
    billingRequestArgs,
    productName,
    order,
    isNueIntegrationEnabled,
    selectedPosSystem,
  } = useInventoryInsightsProvider();

  const refetching = ref(false);
  const upToDatePaymentMethods = ref<BillingPaymentMethod[]>(paymentMethods.value);

  const productsQuery = useProductsQuery({
    enabled: isNueIntegrationEnabled.value,
  });

  const { refetch } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: async () => {
      const response = await fetchBillingPaymentMethods();
      const sortedResponse = sortItems(response, { sortBy: 'id' });
      paymentMethods.value = sortedResponse;

      if (!isEqual(paymentMethods.value, upToDatePaymentMethods.value)) {
        showSkeletonCard.value = false;
        upToDatePaymentMethods.value = paymentMethods.value;
      }

      return sortedResponse;
    },
    enabled: () => refetching.value,
    refetchInterval: 3000,
  });

  const { mutateAsync: draftOrders } = useDraftOrdersPost();

  const { currentStep, isProcessing, isNavForwardDisabled } = useFeatureEnrollmentProvider({
    events: {
      beforeNext: async () => {
        if (paymentMethods.value?.length === 0) {
          showPaymentRequired.value = true;
          throw new Error('There are currently no payment methods configured.');
        }

        showPaymentRequired.value = false;

        if (!isNueIntegrationEnabled.value) return; //TODO: remove after nue integration is complete

        isProcessing.value = true;
        isNavForwardDisabled.value = true;

        // id filtered by nueId in /products response
        const productId = productsQuery.data.value?.find((product) => product.nueId === LEAFLINK_PLUS_ID)?.id || null;

        try {
          order.value = await draftOrders({
            companyId: company.id || 0,
            productId,
          });
        } finally {
          isProcessing.value = false;
          isNavForwardDisabled.value = false;
        }
      },
    },
  });

  const { fetchBillingPaymentMethods } = useSettingsStore();
  const openBillingContactModal = ref<boolean>(false);
  const route = useRoute();
  const router = useRouter();
  const showPaymentRequired = ref(false);
  const showSkeletonCard = ref(false);

  async function updateCompanyBilling() {
    isProcessing.value = true;

    const result = await getCompanyBillingWithPost();

    billingRequestArgs.company = result.company;
    billingRequestArgs.billingEmail = result.billingEmail;
    billingRequestArgs.id = result.id;

    isProcessing.value = false;
  }

  async function linkPlaidAccount(token) {
    try {
      await createCompanyBankAccount({
        email: billingRequestArgs.billingEmail,
        publicToken: token,
        isDefault: false,
      });

      // reload payment methods
      refetch();

      trackWithSegment({
        event: 'inventoryInsightsEnrollmentPaymentMethodsManaged',
        properties: {
          pos_system: selectedPosSystem.value,
          product_name: productName,
          element_engaged: 'Verify with Plaid',
          page_engaged_from: currentStep.value?.text,
        },
      });
    } catch (err) {
      if (isHttpError(err)) {
        notifyError(err, 'Unable to link this bank account.');
      } else {
        throw err;
      }
    }
  }

  function handleAddCreditCardClick() {
    refetching.value = true;

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentPaymentMethodsManaged',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'Add New Credit Card',
        page_engaged_from: currentStep.value?.text,
      },
    });
  }

  function handleAddBankAccountClick() {
    showSkeletonCard.value = true;

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentPaymentMethodsManaged',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'Add New Bank Account',
        page_engaged_from: currentStep.value?.text,
      },
    });
  }

  function handleEditEmailClick() {
    openBillingContactModal.value = true;

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentBillingPOCManaged',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'Edit button',
        page_engaged_from: currentStep.value?.text,
      },
    });
  }

  function handleSetDefaultClick(method: BillingPaymentMethod) {
    const methodType = {
      bankaccount: 'Bank Account',
      paymentcard: 'Credit Card',
    };

    refetch();

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentPaymentMethodsManaged',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: `Set to Default ${methodType[method.contentName]}`,
        page_engaged_from: currentStep.value?.text,
      },
    });
  }

  function handlePlaidLinkClick() {
    trackWithSegment({
      event: 'inventoryInsightsEnrollmentPaymentMethodsManaged',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'Verify with Plaid',
        page_engaged_from: currentStep.value?.text,
      },
    });
  }

  onBeforeMount(async () => {
    await updateCompanyBilling();

    // Fetch payment methods and immediately hold until automatic refresh is triggered upon selecting "Add New Credit Card"
    await refetch();

    //if there's a public token in the URL, that means the user has just gone
    //through the oauth flow and we need to complete it
    if (route && Object.hasOwn(route.query, 'public_token') && route.query.public_token) {
      await linkPlaidAccount(route.query.public_token);

      // remove public token from url
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { public_token, ...queryWithoutPublicToken } = route.query;
      await router.replace({ query: queryWithoutPublicToken });
    }
  });

  onUnmounted(() => {
    refetching.value = false;
    showSkeletonCard.value = false;
  });
</script>

<template>
  <div data-test="billing-setup">
    <div>
      <h3 class="tw-mb-2">{{ i18n.t('inventoryInsights.billingSetup.billingContact') }}</h3>
      <Divider class="tw-mb-9" />
    </div>

    <div class="tw-pb-9">
      <h4 class="tw-pb-3">{{ i18n.t('inventoryInsights.billingSetup.billingUpdates') }}</h4>
      <h4>
        <span v-if="billingRequestArgs" class="tw-font-weight-400 tw-text-ice-700 tw-pr-1.5">
          {{ billingRequestArgs.billingEmail }}
        </span>
        <a
          href="#"
          class="tw-underline tw-text-blue"
          data-test="link|open-billing-contact-modal"
          @click.prevent="handleEditEmailClick"
          >{{ i18n.t('common.edit') }}</a
        >
      </h4>
    </div>

    <div>
      <h3 class="tw-mb-2">{{ i18n.t('common.paymentMethod') }}</h3>
      <Divider class="tw-mb-9" />
      <div class="tw-mb-9">
        <div>
          <div class="tw-flex tw-items-center">
            <p class="tw-mb-0 tw-text-ice-900">{{ i18n.t('inventoryInsights.billingSetup.addNewPaymentMethod') }}</p>
          </div>
          <ul class="tw-list-disc tw-ml-12">
            <li
              v-html="i18n.t('inventoryInsights.billingSetup.creditCards')"
              class="tw-font-weight-400 marker:tw-text-ice-900"
            ></li>
            <li v-html="i18n.t('inventoryInsights.billingSetup.bankAccounts')" class="marker:tw-text-ice-900"></li>
          </ul>
        </div>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-6 tw-mt-6 tw-mb-12 tw-max-w-[600px] md:tw-grid-cols-2">
        <Card data-test="card|add-new-payment-method" class="tw-grid tw-gap-6 tw-p-6 [&>*]:tw-m-0 [&>*]:tw-p-0">
          <CardContent class="tw-grid flex-1 tw-gap-4 tw-py-6 tw-content-center !tw-p-0">
            <h4 class="tw-text-center">
              {{ i18n.t('settings.billing.editPaymentMethodsModal.addPaymentMethods.title') }}
            </h4>

            <AddCreditCard
              v-if="billingRequestArgs.id"
              @click="handleAddCreditCardClick"
              @submit="showSkeletonCard = true"
            />

            <PlaidLink
              v-slot="{ open }"
              :create-token="postLinkToken"
              :env="env.VITE_PLAID_ENV"
              :storage-key="`${env.MODE}-plaid`"
              @success="linkPlaidAccount"
              @click="handleAddBankAccountClick"
              @exit="showSkeletonCard = false"
            >
              <Button class="tw-w-full" secondary color="blue" :disabled="open">
                <span class="tw-flex tw-gap-2 tw-items-center tw-justify-center">
                  <Icon name="money" />
                  {{ $t('settings.billing.editPaymentMethodsModal.addPaymentMethods.addBankAccountLabel') }}
                </span>
              </Button>
            </PlaidLink>
          </CardContent>
        </Card>

        <PaymentMethodListCard
          v-for="method in paymentMethods"
          :key="method.id"
          :payment-method="method"
          :show-trash-icon="false"
          :data-test="`modal|pay-method-${method.id}`"
          @update:set-default="() => handleSetDefaultClick(method)"
          @update:verify-account="refetch"
          @click:plaid-link="handlePlaidLinkClick"
        />

        <Card v-if="showSkeletonCard" data-test="card|skeleton-card">
          <template #top-left>
            <Skeleton width="60" height="20" />
          </template>

          <CardContent>
            <Skeleton width="240" height="20" class="tw-mb-1" />
            <Skeleton width="120" height="20" class="tw-mb-6" />
            <Skeleton width="60" height="20" />
          </CardContent>

          <span class="tw-absolute tw-top-[calc(100%_+_6px)] tw-text-xs">
            {{ i18n.t('inventoryInsights.billingSetup.loadPaymentMethod') }}
          </span>
        </Card>
      </div>
    </div>
    <BillingContactModal :open="openBillingContactModal" @close="openBillingContactModal = false" />
    <Teleport to="#stash-dialogs-mount-node">
      <Dialog
        alert
        :open="showPaymentRequired"
        :confirm-text="i18n.t('inventoryInsights.paymentRequiredDialog.acknowledge')"
        :description="i18n.t('inventoryInsights.paymentRequiredDialog.description')"
        :header="i18n.t('inventoryInsights.paymentRequiredDialog.paymentRequired')"
        data-test="dialog|show-payment-required"
        status="warning"
        @confirm="showPaymentRequired = false"
      />
    </Teleport>
  </div>
</template>
